import { forwardRef, type HTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/cn";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Render onto the child element instead of a <div> (clickable-card links). */
  asChild?: boolean;
  /** Hover lift + glow for clickable cards. */
  interactive?: boolean;
}

/**
 * A soft glass tile (docs/v2-core/STYLE.md): translucent white with a white
 * hairline, generous rounding and a diffuse shadow, so it floats on the
 * page's scene instead of reading as a hard box. Use it for things a parent
 * acts on or compares (a child, a post, a stat); group plain content in an
 * open SectionCard instead of wrapping everything in cards.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, asChild = false, interactive = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      ref={ref}
      className={cn(
        "rounded-[1.75rem] border border-white/80 bg-white/80 p-6 shadow-soft",
        interactive && "transition hover:-translate-y-0.5 hover:bg-white hover:shadow-glow",
        className,
      )}
      {...props}
    />
  );
});
